package org.zest.insan_360_website.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.CSSLengthOrPercentageNumericValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.css.Width
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.RowScope
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.thenIf
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.zest.insan_360_website.components.layouts.*
import org.zest.insan_360_website.data.page_states.dashboard.CreditsDashboardState

@Composable
fun FlushedListRowWrapper(
    gap: CSSLengthOrPercentageNumericValue = 0.cssRem,
    index: Int,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        Modifier
            .padding(0.5.cssRem)
            .fillMaxWidth()
            .fontSize(0.55.cssRem)
            .fontWeight(FontWeight.SemiBold)
            .border {
                width(bottom = 1.px, top = 0.px, left = 0.px, right = 0.px)
                color(userPageBorderStrongColor)
                style(LineStyle.Solid)
            }
            .gap(gap)
            .backgroundColor(if (index % 2 == 0) userPageColor else userPageColor.darkened(0.05f))
            .then(containerShadowStyle.toModifier())
            .then(modifier)
    ) {
        content()
    }
}

@Composable
fun FlushedListContainer(
    flexWeight: Int = 1,
    header: @Composable () -> Unit = {},
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        Modifier
            .flex(flexWeight)
            .fillMaxSize()
            .gap(0.5.cssRem)
    ) {
        header()
        Div(
            Modifier
                .fillMaxSize()
                .position(Position.Relative)
                .backgroundColor(userPageBGColor)
                .border {
                    width(1.px)
                    color(userPageBorderStrongColor)
                    style(LineStyle.Solid)
                }
                .borderRadius(0.5.cssRem)
                .overflow(Overflow.Auto)
                .toAttrs()
        ) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .minHeight(if (CreditsDashboardState.isLoading) 100.percent else 0.percent)
                    .position(Position.Absolute),
                verticalArrangement = Arrangement.Top,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                content()
            }
        }
    }
}

@Composable
fun SectionTitle(title: String, icon: @Composable () -> Unit = {}) {
    Row(
        Modifier.fillMaxWidth().flex(1).fontSize(1.5.cssRem).gap(1.cssRem),
        verticalAlignment = Alignment.CenterVertically
    ) {
        SpanText(title, Modifier.fontWeight(FontWeight.SemiBold).color(userPagePrimaryTextColor))
        icon()
    }
}

@Composable
fun TableFlushedListContainer(
    flexWeight: Int = 1,
    header: @Composable () -> Unit = {},
    listHeaders: @Composable RowScope.() -> Unit,
    listHeadersModifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    Column(
        Modifier
            .flex(flexWeight)
            .fillMaxSize()
            .gap(0.5.cssRem)
    ) {
        header()
        Div(
            Modifier
                .fillMaxSize()
                .position(Position.Relative)
                .backgroundColor(userPageBGColor)
                .border {
                    width(1.px)
                    color(userPageBorderStrongColor)
                    style(LineStyle.Solid)
                }
                .borderRadius(0.5.cssRem)
                .overflow {
                    x(Overflow.Auto)
                    y(Overflow.Auto)
                }
                .toAttrs()
        ) {
            Column(
                Modifier
                    .minWidth(100.percent)
                    .width(Width.FitContent)
                    .minHeight(if (CreditsDashboardState.isLoading) 100.percent else 0.percent)
                    .position(Position.Absolute),
                verticalArrangement = Arrangement.Top,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Row(
                    Modifier
                        .fillMaxWidth()
                        .position(Position.Sticky)
                        .top(0.px)
                        .zIndex(5)
                        .backgroundColor(userPageBGColor)
                        .padding(leftRight = 0.5.cssRem)
                        .border {
                            width(bottom = 1.px, top = 0.px, left = 0.px, right = 0.px)
                            color(userPageBorderStrongColor)
                            style(LineStyle.Solid)
                        }
                        .then(listHeadersModifier),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    listHeaders()
                }
                content()
            }
        }
    }
}

@Composable
fun ListHeader(title: String, isLast: Boolean = false, modifier: Modifier = Modifier) {
    SpanText(
        title,
        modifier
            .fillMaxSize()
            .fontWeight(FontWeight.Medium)
            .fontSize(1.cssRem)
            .padding(topBottom = 0.125.cssRem)
            .thenIf(!isLast) {
                Modifier.border {
                    width(bottom = 0.px, top = 0.px, left = 0.px, right = 1.px)
                    color(userPageBorderStrongColor)
                    style(LineStyle.Solid)
                }
            }
    )
}

@Composable
fun ListEntry(entry: String) {
    SpanText(
        entry,
        Modifier
            .fontSize(0.875.cssRem)
            .whiteSpace(WhiteSpace.NoWrap)
            .overflow(Overflow.Hidden)
            .textOverflow(TextOverflow.Ellipsis)
            .title(entry)
    )
}